import { createSlice, nanoid } from '@reduxjs/toolkit';
import { lookupCleared, operatorPrefChanged, operatorWarehouseSelected } from './operatorSlice';
import { fetchStock } from './operatorThunks';
import { SCAN_SOURCE, cleanCode } from './operatorScanGate';
import { taskKeyOf } from './operatorTaskScope';
import { fetchPickingOrder } from '../operatorPicking/operatorPickingThunks';
import { fetchPickingRoute } from '../operatorPicking/operatorPickingRouteThunks';
import { printPackingLabels, sendToCourier } from '../operatorPacking/operatorPackingThunks';
import { courierWaitStarted } from '../operatorPacking/operatorPackingActions';
import { fetchReceipt } from '../operatorReceiving/operatorReceivingThunks';
import { fetchTasks } from '../operatorTasks/operatorTasksThunks';
import { isBackgroundRequest } from '../feedback/feedbackSlice';

/**
 * Work beside the screen — Consulta's stock, the tray, the labels PDF, a list
 * asked again in the background —: a held read is never theirs.
 */
const isSideReadRejection = (action) =>
  fetchStock.rejected.match(action) ||
  fetchTasks.rejected.match(action) ||
  printPackingLabels.rejected.match(action) ||
  isBackgroundRequest(action);

/**
 * Reloads a screen takes reads through: picking — an order or a route — and
 * receiving accept a code while their task reloads, so a read held there
 * waits for a write. Packing waits for its reload, and that one failing drops
 * the read.
 */
const isUnawaitedReloadRejection = (action) =>
  fetchPickingOrder.rejected.match(action) ||
  fetchPickingRoute.rejected.match(action) ||
  fetchReceipt.rejected.match(action);

/** The task a keyed write is of, as its arg names it: an order, a receipt, a route or a kit. */
const taskIdOfWrite = (arg) => arg?.orderId ?? arg?.receiptId ?? arg?.routeId ?? arg?.kitId;

const OPERATOR_OUTCOME = /^operator\w*\/.+\/(fulfilled|rejected)$/;

/** A camera that stops again this soon after it was remounted will not come back by itself. */
const CAMERA_STALL_AGAIN_MS = 10000;

/** Whether the read waiting is the camera's: a typed one does not depend on the camera. */
export const isCameraHeld = (held) => Boolean(held) && !held.source;

/** Drops the read waiting. A typed one is said: unlike the camera, nothing reads it again. */
const dropHeld = (state) => {
  if (state.held?.source) state.dropped = { code: state.held.code };
  state.held = null;
};

/** An operator thunk that went through. */
export const isOperatorSuccess = (action) => OPERATOR_OUTCOME.exec(action.type)?.[1] === 'fulfilled';

/** An operator thunk that failed; a read the gate turned away is not a failure. */
export const isOperatorRejection = (action) =>
  OPERATOR_OUTCOME.exec(action.type)?.[1] === 'rejected' && !action.meta?.condition;

/*
 * The state of the device in the operator's hand: the band that says how the
 * last action went, the camera, the audio and the sheets open over a screen.
 * Never persisted: none of it means anything after a reload.
 */
const initialState = {
  /**
   * `{id, tone, message, detail?, retry?, action?, at}`; tone ∈ success | error | warning | step.
   * `action` is a second button the band offers, such as «Deshacer» (see `operatorRetry`).
   */
  signal: null,
  /** The screen the band belongs to, `{flow, key}`. */
  task: null,
  /**
   * `turnedAway`: the last camera read the store rejected, `{codes, id}` — the
   * code read and those the thunk tried beside it (`meta.tried`). The camera
   * offers again what was beside them once they are out of view. `stalledAt`:
   * when the camera last stopped without an error (`cameraStalled`).
   */
  camera: { error: null, epoch: 0, turnedAway: null, stalledAt: null },
  /**
   * A read that came in while the screen was busy, `{code, alternatives,
   * again?, source?}` — the camera's, or one typed by a reader (`source`). It
   * waits here: a listener sends it if what kept the screen busy went well, and
   * a failure of that drops it. What the screen was not waiting for leaves it
   * be: Consulta's stock, the labels PDF, a reload of the order or receipt
   * being picked or counted, an answer of a task no longer on screen, and one
   * an earlier visit to it sent.
   */
  held: null,
  /**
   * A read that was not read, `{code}`: held and dropped by a failure or by a
   * later read, or typed while a screen that holds nothing was busy. The scan
   * panel says so until the next read.
   */
  dropped: null,
  /** `locked` until a tap lets iOS play sound, then `ready`; `unavailable` without Web Audio. */
  audio: 'locked',
  /** Sheets open right now; the camera pauses while any is. */
  sheets: 0,
  /** When the operator last touched the screen, by the phone's clock: no list moves under a finger. */
  lastTouchAt: null,
};

const operatorDeviceSlice = createSlice({
  name: 'operatorDevice',
  initialState,
  reducers: {
    signalRaised: {
      reducer: (state, action) => {
        state.signal = action.payload;
      },
      prepare: (signal) => ({ payload: { id: nanoid(), at: Date.now(), ...signal } }),
    },
    signalDismissed: (state) => {
      state.signal = null;
    },
    /**
     * A flow screen opened. The band clears when the operator moves to another
     * flow or opens another task, not on every navigation: "REC-000090
     * cerrada" has to survive the trip back to the queue.
     *
     * @param {{payload: {flow: string, taskId?: string|number|null}}} action
     */
    taskEntered: (state, action) => {
      const { flow, taskId = null } = action.payload;
      const key = taskKeyOf(flow, taskId);
      const anotherFlow = state.task?.flow !== flow;
      const anotherTask = taskId !== null && state.task?.key !== key;
      const retry = state.signal?.retry;
      // A lost keyed write stays offered when its own task is opened: that screen has no other way to send it.
      const retryOfThisTask = retry && typeof retry === 'object' && taskKeyOf(retry.flow, taskIdOfWrite(retry.arg)) === key;
      // A retry its slice keeps only goes from the task it was on.
      const retryLeftBehind = typeof retry === 'string' && state.task?.key !== key;
      if (!retryOfThisTask && (anotherFlow || anotherTask || retryLeftBehind)) state.signal = null;
      state.task = { flow, key };
      state.held = null;
      state.dropped = null;
    },
    /** A tap anywhere while the audio is locked; a listener unlocks it inside the gesture. */
    operatorGestured: () => {},
    /** @param {{payload: number}} action - When a finger came down anywhere on the screen. */
    operatorTouched: (state, action) => {
      state.lastTouchAt = action.payload;
    },
    audioStateChanged: (state, action) => {
      state.audio = action.payload;
    },
    /** @param {{payload: {kind: string, message: string}}} action */
    cameraFailed: (state, action) => {
      state.camera.error = action.payload;
      if (isCameraHeld(state.held)) state.held = null;
    },
    /** Remounts the camera: the library has no other way back from an error. */
    cameraRetried: (state) => {
      state.camera.error = null;
      state.camera.epoch += 1;
    },
    /**
     * The camera stopped without an error: the page slept (`slept`), or its
     * track ended or sent no image. It is remounted, as «Reintentar» does; a
     * track that stops again within `CAMERA_STALL_AGAIN_MS` is a failure the
     * operator sees, with «Reintentar». Waking up always remounts.
     */
    cameraStalled: {
      reducer: (state, action) => {
        const { reason, at } = action.payload;
        const again =
          reason !== 'slept' && state.camera.stalledAt !== null && at - state.camera.stalledAt < CAMERA_STALL_AGAIN_MS;
        state.camera.stalledAt = at;
        if (again) {
          state.camera.error = { kind: 'stopped', message: reason };
          if (isCameraHeld(state.held)) state.held = null;
          return;
        }
        state.camera.epoch += 1;
      },
      prepare: ({ reason }) => ({ payload: { reason, at: Date.now() } }),
    },
    /**
     * `again` is kept only when true: the camera saw the code come back into
     * view, which a flow that counts each read needs to count it. `source` is
     * kept only when not the camera's: a typed read goes through the scan gate
     * as typed. Only one read waits: one already waiting is dropped, and said.
     *
     * @param {{payload: {code: string, alternatives?: string[], again?: boolean, source?: string}}} action
     */
    cameraReadHeld: (state, action) => {
      const { code, alternatives, again, source } = action.payload;
      state.dropped = state.held ? { code: state.held.code } : null;
      state.held = {
        code,
        alternatives: alternatives || [],
        ...(again ? { again: true } : {}),
        ...(source && source !== SCAN_SOURCE.CAMERA ? { source } : {}),
      };
    },
    /** @param {{payload: {code: string}}} action - A typed read while busy, which this screen does not hold. */
    readDropped: (state, action) => {
      state.dropped = { code: action.payload.code };
    },
    heldReadReleased: (state) => {
      state.held = null;
    },
    // A paused camera reads what is in view again when it comes back.
    sheetOpened: (state) => {
      state.sheets += 1;
      dropHeld(state);
    },
    sheetClosed: (state) => {
      state.sheets = Math.max(0, state.sheets - 1);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(operatorPrefChanged, (state, action) => {
        if (action.payload.pref === 'camera' && !action.payload.value && isCameraHeld(state.held)) state.held = null;
      })
      // What it waited for is gone with what Limpiar cleared.
      .addCase(lookupCleared, (state) => {
        state.held = null;
      })
      // A band is about the warehouse it was raised in; its «Reintentar» would go to the new one.
      .addCase(operatorWarehouseSelected, (state) => {
        state.signal = null;
        state.held = null;
      })
      // An order sent to its courier takes no read from before: once its labels are in, it would land on a
      // shipment the courier already made.
      .addCase(sendToCourier.fulfilled, (state, action) => {
        if (!action.meta.offScreenTask) dropHeld(state);
      })
      .addCase(courierWaitStarted, (state) => {
        dropHeld(state);
      })
      // A failure keeps its band, and its «Reintentar», on screen. Neither work beside the screen —
      // Consulta's stock, the tray, the labels PDF — nor a reload of picking or receiving, nor a write
      // of a task off screen, nor one an earlier visit to the task on screen sent, is what the held
      // read waits for.
      .addMatcher(
        (action) =>
          isOperatorRejection(action) &&
          !isSideReadRejection(action) &&
          !isUnawaitedReloadRejection(action) &&
          !action.meta?.offScreenTask &&
          !action.meta?.earlierVisit,
        (state) => {
          if (state.held) state.dropped = { code: state.held.code };
          state.held = null;
        }
      )
      // Only reads carry the camera as their source: a write's failure gives nothing back.
      .addMatcher(
        (action) =>
          isOperatorRejection(action) &&
          action.meta?.arg?.source === SCAN_SOURCE.CAMERA &&
          !action.meta.offScreenTask &&
          !action.meta.earlierVisit,
        (state, action) => {
          state.camera.turnedAway = {
            codes: [cleanCode(action.meta.arg.code), ...(action.meta.tried || [])],
            id: action.meta.requestId,
          };
        }
      )
      // The next read is what the panel talks about.
      .addMatcher(
        (action) => /\/pending$/.test(action.type) && Boolean(action.meta?.arg?.source),
        (state) => {
          state.dropped = null;
        }
      );
  },
});

export const {
  signalRaised,
  signalDismissed,
  taskEntered,
  operatorGestured,
  operatorTouched,
  audioStateChanged,
  cameraFailed,
  cameraRetried,
  cameraStalled,
  cameraReadHeld,
  readDropped,
  heldReadReleased,
  sheetOpened,
  sheetClosed,
} = operatorDeviceSlice.actions;

export default operatorDeviceSlice.reducer;
